type CredentialResponse = { credential?: string };

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  prompt: (
    listener?: (notification: {
      isNotDisplayed: () => boolean;
      isSkippedMoment: () => boolean;
      isDismissedMoment: () => boolean;
    }) => void,
  ) => void;
  renderButton: (
    parent: HTMLElement,
    options: { theme?: string; size?: string; text?: string; width?: number },
  ) => void;
};

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

let scriptPromise: Promise<void> | null = null;

export function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Google Sign-In failed to load")),
      );
      if (window.google?.accounts?.id) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google Sign-In failed to load"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function showFallbackButton(
  id: GoogleAccountsId,
  clientId: string,
  resolve: (token: string) => void,
  reject: (error: Error) => void,
) {
  const host = document.createElement("div");
  host.setAttribute("data-caprica-google-signin", "1");
  host.style.cssText =
    "position:fixed;inset:0;display:grid;place-items:center;background:rgba(0,0,0,.55);z-index:10000";
  const panel = document.createElement("div");
  panel.style.cssText =
    "background:#111722;padding:20px;border-radius:12px;border:1px solid #333d54;min-width:300px";
  const label = document.createElement("p");
  label.textContent = "Continue with Google";
  label.style.cssText = "margin:0 0 12px;color:#fff;font:650 14px/1.4 system-ui";
  const buttonHost = document.createElement("div");
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.textContent = "Cancel";
  cancelBtn.style.cssText =
    "margin-top:14px;background:transparent;border:0;color:#8994a9;cursor:pointer";
  panel.append(label, buttonHost, cancelBtn);
  host.append(panel);
  document.body.append(host);

  const cleanup = () => host.remove();
  cancelBtn.onclick = () => {
    cleanup();
    reject(new Error("Google sign-in was cancelled"));
  };
  host.addEventListener("click", (event) => {
    if (event.target === host) {
      cleanup();
      reject(new Error("Google sign-in was cancelled"));
    }
  });

  id.initialize({
    client_id: clientId,
    callback: (response) => {
      cleanup();
      if (response.credential) resolve(response.credential);
      else reject(new Error("Google did not return a credential"));
    },
  });
  id.renderButton(buttonHost, {
    theme: "outline",
    size: "large",
    text: "signin_with",
    width: 280,
  });
}

/** Opens Google One Tap, falling back to a GIS button overlay. */
export async function requestGoogleIdToken(clientId: string): Promise<string> {
  await loadGoogleIdentityServices();
  const id = window.google?.accounts?.id;
  if (!id) throw new Error("Google Sign-In is unavailable in this browser");

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (token: string) => {
      if (settled) return;
      settled = true;
      resolve(token);
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      reject(new Error(message));
    };

    id.initialize({
      client_id: clientId,
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: (response) => {
        if (response.credential) finish(response.credential);
        else fail("Google did not return a credential");
      },
    });

    id.prompt((notification) => {
      if (
        notification.isNotDisplayed() ||
        notification.isSkippedMoment() ||
        notification.isDismissedMoment()
      ) {
        if (settled) return;
        showFallbackButton(id, clientId, finish, (error) => fail(error.message));
      }
    });
  });
}
